import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { RefreshCw, History } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTION_COLORS = {
  'product.create': 'bg-green-100 text-green-800 border-green-200',
  'product.update': 'bg-blue-100 text-blue-800 border-blue-200',
  'product.delete': 'bg-red-100 text-red-700 border-red-200',
  'product.import': 'bg-green-100 text-green-800 border-green-200',
  'order.status': 'bg-purple-100 text-purple-800 border-purple-200',
  'team.add': 'bg-amber-100 text-amber-800 border-amber-200',
  'team.update': 'bg-amber-100 text-amber-800 border-amber-200',
  'team.remove': 'bg-red-100 text-red-700 border-red-200',
};

const ActivityLog = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchLogs = async () => {
    try {
      const token = localStorage.getItem('vendor_token');
      const { data } = await axios.get(`${API}/vendor/activity`, { headers: { Authorization: `Bearer ${token}` } });
      setLogs(data.logs || []);
    } catch {
      toast.error('Failed to load activity');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLogs(); }, []);

  return (
    <div className="space-y-5">
      <div className="flex justify-between items-center gap-3">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">Activity</h2>
          <p className="text-muted-foreground mt-1 text-sm">Who did what, and when — across your team.</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchLogs} data-testid="refresh-activity">
          <RefreshCw className="h-4 w-4 mr-2" /> Refresh
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center p-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>
      ) : logs.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <History className="h-12 w-12 text-muted-foreground/40 mb-3" />
            <p className="text-muted-foreground">No activity yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {logs.map((l) => (
            <Card key={l.id}>
              <CardContent className="flex items-start gap-3 py-3">
                <Badge variant="outline" className={`mt-0.5 shrink-0 ${ACTION_COLORS[l.action] || ''}`}>
                  {l.action.replace('.', ' ')}
                </Badge>
                <div className="flex-1 min-w-0">
                  <p className="text-sm">{l.summary}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    <span className="font-medium">{l.actor_name}</span>
                    <span className="capitalize"> ({l.actor_type})</span>
                    {' · '}
                    {l.created_at ? new Date(l.created_at).toLocaleString() : ''}
                  </p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default ActivityLog;
